import { LegalLayout } from "@/components/LegalLayout";

const DisputesPolicy = () => (
  <LegalLayout
    title="Dispute Policy"
    description="How Cash Isle resolves disagreements over gigs, requests, and transactions."
    updated="May 14, 2026"
  >
    <p>
      A dispute is a formal request for Cash Isle to review a transaction or
      gig and decide an outcome. Most issues are best resolved directly with
      the counterparty first — opening a dispute should be a last step.
    </p>

    <h2>When you can dispute</h2>
    <ul>
      <li><strong>Gig escrow:</strong> within the dispute window after proof is submitted, or any time before auto-release.</li>
      <li><strong>Money requests:</strong> if a request was paid in error or under coercion, within 7 days.</li>
      <li><strong>Merchant payments:</strong> if the merchant fails to honour their stated refund policy, or if you suspect fraud.</li>
      <li><strong>Account issues:</strong> unauthorised transactions, suspected account takeover, or duplicate charges — report immediately.</li>
    </ul>

    <h2>How to open a dispute</h2>
    <ol>
      <li>Open the relevant gig, request, or transaction in-app.</li>
      <li>Tap <em>Open dispute</em> and select a reason.</li>
      <li>Describe what happened in plain language.</li>
      <li>Attach evidence: photos, screenshots, chat history, timestamps, GPS.</li>
      <li>Submit. Funds remain held in escrow (for gigs) until the dispute resolves.</li>
    </ol>

    <h2>Evidence we look at</h2>
    <ul>
      <li>Proof-of-work media and GPS submitted by the worker.</li>
      <li>Original gig description, agreed scope, and budget.</li>
      <li>In-app messages between the parties.</li>
      <li>Device, IP, and behavioural fraud signals.</li>
      <li>Counterparty's history (completion rate, prior disputes).</li>
      <li>Any AI-generated risk summary attached to the case.</li>
    </ul>

    <h2>Possible outcomes</h2>
    <ul>
      <li><strong>Release:</strong> full budget paid to the worker.</li>
      <li><strong>Refund:</strong> full budget returned to the poster.</li>
      <li><strong>Split:</strong> partial release to the worker, remainder refunded to the poster.</li>
      <li><strong>Reversal:</strong> for non-gig transactions, funds may be clawed back where fraud or unauthorised use is found.</li>
      <li><strong>Account action:</strong> warnings, freezes, tier downgrades, or bans for the party at fault.</li>
    </ul>

    <h2>Timelines</h2>
    <ul>
      <li>Initial review: within <strong>3 business days</strong> of submission.</li>
      <li>Complex cases requiring extra evidence: up to <strong>10 business days</strong>.</li>
      <li>Appeals: reply to the resolution email within <strong>7 days</strong> with new evidence; a senior reviewer responds within 5 business days.</li>
    </ul>

    <h2>Bad-faith disputes</h2>
    <p>
      Filing fake disputes, fabricating evidence, or coordinating with another
      account to extract funds is a serious violation of our{" "}
      <a href="/legal/acceptable-use">Acceptable Use Policy</a>. Consequences
      include reversal of disputed funds, account ban, hustle-score penalty,
      and possible referral to law enforcement.
    </p>

    <h2>Final decisions</h2>
    <p>
      Resolutions by Cash Isle are final after the appeal window closes. We
      are not a court — we make practical decisions based on the evidence and
      our policies. If you need legal recourse beyond our process, see the
      arbitration section of the <a href="/legal/terms">Terms of Service</a>.
    </p>
  </LegalLayout>
);

export default DisputesPolicy;
